import { useMemo, useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  getFirebaseClient,
  useAuth,
  consultationConditionLabel,
  parseConsultationListRow,
  type ConsultationListRow,
} from "@workspace/admin-ui";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { PageHeader } from "@/components/AppShell";

interface PharmacyOption {
  id: string;
  displayName: string;
}

// Task #438 — Chain Admin Consultations tab. Lists completed consultations
// synced from pharmacist devices across every pharmacy in the chain, with
// the originating pharmacy shown per record (Chain Admin's key difference
// from Pharmacy Admin's Consultations tab).
export default function ConsultationsPage() {
  const { membership } = useAuth();
  const chainId = membership?.chainId ?? "";
  const { db } = getFirebaseClient();
  const [search, setSearch] = useState("");
  const [pharmacyFilter, setPharmacyFilter] = useState<string>("all");

  const pharmaciesQ = useQuery<PharmacyOption[]>({
    queryKey: ["chain", chainId, "pharmacies", "consultations-filter"],
    queryFn: async () => {
      const snap = await getDocs(
        query(collection(db, "chains", chainId, "pharmacies"), orderBy("displayName")),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        displayName: String((d.data() as Record<string, unknown>).displayName ?? d.id),
      }));
    },
    enabled: !!chainId,
  });

  const pharmacyMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of pharmaciesQ.data ?? []) map.set(p.id, p.displayName);
    return map;
  }, [pharmaciesQ.data]);

  const consultationsQ = useQuery<ConsultationListRow[]>({
    queryKey: ["consultations", chainId, "chain-wide"],
    queryFn: async () => {
      const snap = await getDocs(
        query(
          collection(db, "consultations"),
          where("chainId", "==", chainId),
          orderBy("createdAt", "desc"),
        ),
      );
      return snap.docs.map((d) => parseConsultationListRow(d.id, d.data()));
    },
    enabled: !!chainId,
  });

  const rows = consultationsQ.data ?? [];
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (pharmacyFilter !== "all" && r.pharmacyId !== pharmacyFilter) return false;
      if (!term) return true;
      return (
        r.patientName.toLowerCase().includes(term) ||
        r.pharmacistName.toLowerCase().includes(term) ||
        consultationConditionLabel(r.condition).toLowerCase().includes(term) ||
        (pharmacyMap.get(r.pharmacyId) ?? "").toLowerCase().includes(term)
      );
    });
  }, [rows, search, pharmacyFilter, pharmacyMap]);

  return (
    <>
      <PageHeader
        title="Consultations"
        description="Completed consultations synced from pharmacist devices across the chain."
      />
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Paper>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
            }}
          >
            <TextField
              placeholder="Search by patient, pharmacist, pharmacy, or condition"
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="pharmacy-filter-label">Pharmacy</InputLabel>
              <Select
                labelId="pharmacy-filter-label"
                label="Pharmacy"
                value={pharmacyFilter}
                onChange={(e) => setPharmacyFilter(e.target.value)}
              >
                <MenuItem value="all">All pharmacies</MenuItem>
                {(pharmaciesQ.data ?? []).map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {consultationsQ.isLoading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <CircularProgress size={20} />
            </Box>
          ) : consultationsQ.error ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="error">
                {(consultationsQ.error as Error).message}
              </Typography>
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">
                {rows.length === 0
                  ? "No consultations synced yet."
                  : "No consultations match your filters."}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filtered.map((c, i) => (
                <Box key={c.id}>
                  <ListItemButton
                    component={Link}
                    href={`/consultations/${c.id}`}
                    sx={{ py: 1.5, px: 2 }}
                  >
                    <ListItemText
                      primary={
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: "center", flexWrap: "wrap" }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {c.patientName || "Unnamed patient"}
                          </Typography>
                          <Chip
                            label={consultationConditionLabel(c.condition)}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={pharmacyMap.get(c.pharmacyId) ?? c.pharmacyId}
                            size="small"
                            color="default"
                          />
                        </Stack>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          Pharmacist: {c.pharmacistName || c.pharmacistUid} ·
                          Completed{" "}
                          {c.completedAt
                            ? new Date(c.completedAt).toLocaleString()
                            : "—"}
                        </Typography>
                      }
                    />
                    <ChevronRightIcon fontSize="small" color="disabled" />
                  </ListItemButton>
                  {i < filtered.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          )}
        </Paper>
      </Box>
    </>
  );
}
